if (!customElements.get('product-message')) {
  class ProductMessage extends HTMLElement {
    constructor() {
      super();
      this.closeButton = this.querySelector('.js-close-message');
      this.openInEditor = false;
      this.variantMode = this.hasAttribute('data-variant-message');
      this.dismissedVariants = new Set();
      this.initialDelay = this.style.transitionDelay;

      this.init();
      this.bindEvents();
    }

    connectedCallback() {
      if (!this.variantMode) return;
      this.productSection = this.closest('.js-product');
      this.variantChangeHandler = (evt) => {
        if (evt.target.closest('.js-product') !== this.productSection) return;
        this.updateVariantMessage(evt.detail.variant?.id, false);
      };
      this.productSection?.addEventListener('on:variant:change', this.variantChangeHandler);
      this.updateVariantMessage(this.dataset.currentVariantId, true);
    }

    updateVariantMessage(variantId, initial) {
      clearTimeout(this.messageTimer);
      clearTimeout(this.hideTimer);
      this.variantId = variantId ? String(variantId) : '';
      const content = this.querySelector('[data-message-content]');
      const message = Array.from(this.querySelectorAll('template[data-variant-message-id]'))
        .find((item) => item.dataset.variantMessageId === this.variantId);
      content.replaceChildren(...(message ? [message.content.cloneNode(true)] : []));
      this.variantHasContent = content.textContent.trim().length > 0;
      this.classList.add('product-message--out');
      this.style.removeProperty('filter');
      this.style.removeProperty('pointer-events');
      this.style.removeProperty('max-height');
      this.style.removeProperty('transition-duration');
      this.style.transitionDelay = initial ? this.initialDelay : '0s';
      this.style.display = 'none';
      const closedMessages = this.dataset.persistentClose
        ? theme.storageUtil.get('closed-messages', true) || [] : [];
      if (!this.variantHasContent) return;
      if (!Shopify.designMode && (this.dismissedVariants.has(this.variantId)
        || closedMessages.includes(this.variantCloseKey()))) return;
      this.style.removeProperty('display');
      this.show(!this.dataset.transient || !initial || this.openInEditor);
      if (this.dataset.transient && !this.openInEditor) {
        this.messageTimer = setTimeout(() => {
          if (!this.openInEditor) this.hide(false);
        }, Number(this.dataset.transient) * 1000);
      }
    }

    variantCloseKey() {
      return [this.dataset.blockId, this.dataset.productId, this.variantId].join(':');
    }

    disconnectedCallback() {
      clearTimeout(this.messageTimer);
      clearTimeout(this.hideTimer);
      if (this.variantChangeHandler) {
        this.productSection?.removeEventListener('on:variant:change', this.variantChangeHandler);
      }
      if (this.blockSelectHandler) {
        document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      }

      if (this.blockDeselectHandler) {
        document.removeEventListener('shopify:block:deselect', this.blockDeselectHandler);
      }
    }

    /**
     * Decide whether to show the message, and whether to hide it again (if it's transient)
     */
    init() {
      if (this.variantMode) return;
      let preventShow = false;

      if (this.dataset.persistentClose && !Shopify.designMode) {
        const closedMessages = theme.storageUtil.get('closed-messages', true) || [];
        if (closedMessages && closedMessages.includes(this.dataset.blockId)) {
          preventShow = true;
          this.remove();
        }
      }

      if (this.classList.contains('product-message--out') && (!preventShow || Shopify.designMode)) {
        this.show(!this.dataset.transient);

        if (this.dataset.transient) {
          setTimeout(() => {
            if (!this.openInEditor) {
              this.hide(false);
            }
          }, this.dataset.transient * 1000);
        }
      }
    }

    /**
     * Handle close and TE events
     */
    bindEvents() {
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.handleCloseClick.bind(this));
      }

      if (Shopify.designMode && this.dataset.transient) {
        this.blockSelectHandler = this.handleBlockSelect.bind(this);
        this.blockDeselectHandler = this.handleBlockDeselect.bind(this);
        document.addEventListener('shopify:block:select', this.blockSelectHandler);
        document.addEventListener('shopify:block:deselect', this.blockDeselectHandler);
      }
    }

    /**
     * Decide whether to show the message or not
     * @param {object} evt - Event object
     */
    handleBlockSelect(evt) {
      if (evt.target === this && this.dataset.transient) {
        this.openInEditor = true;
        if (this.variantMode) this.updateVariantMessage(this.variantId, false);
        else this.show(true);
      }
    }

    /**
     * Decide whether to hide the message or not
     * @param {object} evt - Event object
     */
    handleBlockDeselect(evt) {
      if (evt.target === this && this.dataset.transient) {
        this.openInEditor = false;
        this.hide(true);
        if (!this.variantMode) theme.storageUtil.remove('closed-messages');
      }
    }

    /**
     * Closes the message and stores its closure in localStorage when appropriate
     */
    handleCloseClick() {
      if (this.variantMode) {
        clearTimeout(this.messageTimer);
        this.dismissedVariants.add(this.variantId);
        if (this.dataset.persistentClose) {
          const closedMessages = theme.storageUtil.get('closed-messages', true) || [];
          const key = this.variantCloseKey();
          if (!closedMessages.includes(key)) {
            closedMessages.push(key);
            theme.storageUtil.set('closed-messages', closedMessages);
          }
        }
        this.hide(true);
        return;
      }
      if (this.dataset.persistentClose) {
        const closedMessages = theme.storageUtil.get('closed-messages', true) || [];
        if (!closedMessages.includes(this.dataset.blockId)) {
          closedMessages.push(this.dataset.blockId);
          theme.storageUtil.set('closed-messages', closedMessages);
        }
      }
      this.hide(true);
    }

    /**
     * Shows the message
     * @param {boolean} instant - If the message should animate or not
     */
    show(instant) {
      if (instant) {
        this.style.transitionDelay = '0s';
        this.style.transitionDuration = '0s';
      }
      this.classList.remove('product-message--out');
    }

    /**
     * Hides the message
     * @param {boolean} instant - If the message should animate or not
     */
    hide(instant) {
      if (instant) {
        this.style.transitionDelay = '0s';
        this.style.transitionDuration = '0.2s';
        this.style.filter = 'opacity(0)'; // Use a non-transitioning property
        this.style.pointerEvents = 'none';
      }

      if (this.classList.contains('product-message--info')) {
        this.style.maxHeight = `${this.getBoundingClientRect().height + 10}px`;
        this.hideTimer = setTimeout(() => {
          // Delay to trigger css transition
          this.classList.add('product-message--out');
        });
      } else {
        this.classList.add('product-message--out');
      }
    }
  }

  customElements.define('product-message', ProductMessage);
}
